import { Box, Button, Stack, Typography } from '@mui/material';
import { Assessment, FilterKeys, GetMarkbookParams, useGetMarkbookQueries } from '@schooly/api';
import { useGridApiProvider } from '@schooly/components/data-grid';
import { useNotifications } from '@schooly/components/notifications';
import { EmptyFolderSvg } from '@schooly/style';
import { FC, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { MarkbookForGroupsStudentsHeaderCellProps } from '../common/MarkbookCell';
import {
  MarkbookBaseline,
  MarkbookBaselineConfig,
  MarkbookCategoryWithStudents,
  MarkbookForGroupsProps,
  MarkbookStudentWithEntries,
} from '../common/types';
import { getBaseline } from '../common/utils';
import { MarkbookForGroups } from '../MarkbookForGroups';

type MarkbookPreviewTableProps = {
  queriesParams: { id: string; params: GetMarkbookParams }[];
  onEditMarkbook: () => void;
  children?: (assessments: Assessment[]) => React.ReactNode;
  baselineConfig: MarkbookBaselineConfig | null;
  onSetBaselineConfig?: (v: MarkbookBaselineConfig | null) => void;
} & Pick<
  MarkbookForGroupsProps,
  | 'columnArrangeBy'
  | 'sort'
  | 'isSorting'
  | 'onChangeSort'
  | 'onCellClickOverride'
  | 'showPercentage'
  | 'onSuccessfulEntryUpdate'
  | 'getAssessmentEntryColor'
  | 'getAverageCellColor'
  | 'lists'
  | 'assessments'
  | 'showStudentsAverage'
>;

export const MarkbookPreviewTable: FC<MarkbookPreviewTableProps> = ({
  queriesParams,
  assessments,
  lists,
  onEditMarkbook,
  onChangeSort,
  isSorting,
  sort,
  columnArrangeBy,
  showPercentage,
  onSuccessfulEntryUpdate,
  getAssessmentEntryColor,
  getAverageCellColor,
  onCellClickOverride,
  children,
  baselineConfig,
  onSetBaselineConfig,
  showStudentsAverage,
}) => {
  const { onApiRefReady, renderWithProvider } = useGridApiProvider();
  const { queries, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, setParams } =
    useGetMarkbookQueries(queriesParams, { enabled: false });
  const { showError } = useNotifications();

  const { categories, groupsToShowMap } = useMemo(() => {
    const categoryIds = Array.from(queries.keys());
    const groupsToShowMap: Record<string, Set<string>> = {};

    const categories = categoryIds.reduce<MarkbookCategoryWithStudents[]>((acc, curr) => {
      const query = queries.get(curr);
      if (!query) return acc;

      const category = query.data?.pages[0][0].category;
      if (!category) return acc;

      const students =
        query.data?.pages.reduce<MarkbookStudentWithEntries[]>(
          (acc, curr) => [...acc, ...curr[0].students.results],
          [],
        ) ?? [];

      students.forEach(({ assessment_entries }) => {
        assessment_entries.forEach(({ group, assessment_id }) => {
          if (!groupsToShowMap[assessment_id]) {
            groupsToShowMap[assessment_id] = new Set();
          }
          groupsToShowMap[assessment_id].add(group.id);
        });
      });

      const totalStudents = query.data?.pages[0][0].students.count ?? 0;

      return [...acc, { category, students, totalStudents }];
    }, []);

    return { categories, groupsToShowMap };
  }, [queries]);

  const { studentCount, assessmentsWithFilteredGroups, baseline } = useMemo(() => {
    const studentCount = categories.reduce((acc, c) => acc + c.totalStudents, 0);

    const assessmentsWithFilteredGroups = assessments
      .filter((a) => groupsToShowMap[a.id])
      .map((a) => {
        const groupsToShow = groupsToShowMap[a.id];
        return { ...a, groups: a.groups.filter((group) => groupsToShow.has(group.id)) };
      });

    const baseline: MarkbookBaseline | undefined =
      columnArrangeBy === FilterKeys.Subject
        ? getBaseline(assessmentsWithFilteredGroups, baselineConfig)
        : undefined;
    return { studentCount, assessmentsWithFilteredGroups, baseline };
  }, [assessments, categories, groupsToShowMap, baselineConfig, columnArrangeBy]);

  useEffect(() => {
    queriesParams.forEach(({ id, params }) => {
      setParams(id, params);
    });
  }, [queriesParams, setParams]);

  const studentsHeaderCellProps: MarkbookForGroupsStudentsHeaderCellProps = useMemo(() => {
    return { kind: 'students', count: studentCount, textId: 'userType-student-plural' };
  }, [studentCount]);

  if (!queriesParams.length || studentCount === 0) {
    return (
      <Box height="100%">
        <MarkbookPreviewTableEmptyStub
          textId="assessments-markbook-noStudentsForCriteria"
          subTextId="assessments-markbook-noStudentsForCriteriaDescription"
          onClick={onEditMarkbook}
        />
      </Box>
    );
  }

  if (!assessmentsWithFilteredGroups.length) {
    return (
      <Box height="100%">
        <MarkbookPreviewTableEmptyStub
          textId="assessments-markbook-noAssessmentsForCriteria"
          subTextId="assessments-markbook-noAssessmentsForCriteriaDescription"
          onClick={onEditMarkbook}
        />
      </Box>
    );
  }

  return (
    <Stack height="100%">
      <Box flex={1} overflow="hidden">
        <MarkbookForGroups
          showStudentsAverage={showStudentsAverage}
          baseline={baseline}
          onSetBaselineConfig={onSetBaselineConfig}
          assessments={assessmentsWithFilteredGroups}
          categories={categories}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          refetch={refetch}
          lists={lists}
          studentsHeaderCellProps={studentsHeaderCellProps}
          onChangeSort={onChangeSort}
          isSorting={isSorting}
          sort={sort}
          columnArrangeBy={columnArrangeBy}
          showPercentage={showPercentage}
          getAssessmentEntryColor={getAssessmentEntryColor}
          getAverageCellColor={getAverageCellColor}
          onSuccessfulEntryUpdate={onSuccessfulEntryUpdate}
          onApiRefReady={onApiRefReady}
          onUpdateError={showError}
          onCellClickOverride={onCellClickOverride}
        />
      </Box>
      {renderWithProvider(children?.(assessmentsWithFilteredGroups))}
    </Stack>
  );
};

type MarkbookPreviewTableEmptyStubProps = {
  textId: string;
  subTextId: string;
  onClick: () => void;
};

const MarkbookPreviewTableEmptyStub: FC<MarkbookPreviewTableEmptyStubProps> = ({
  textId,
  subTextId,
  onClick,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Stack gap={1} justifyContent="center" alignItems="center" position="relative" height="100%">
      <Box sx={{ svg: { width: 300 } }}>
        <EmptyFolderSvg />
      </Box>
      <Typography variant="h1" textAlign="center" pt={7} sx={{ whiteSpace: 'pre-wrap' }}>
        {formatMessage({ id: textId })}
      </Typography>
      <Typography variant="h3" color="text.primary" textAlign="center">
        {formatMessage({ id: subTextId })}
      </Typography>
      <Button onClick={onClick} sx={{ mt: 2 }}>
        {formatMessage({ id: 'assessments-markbook-editMarkbook' })}
      </Button>
    </Stack>
  );
};
